import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { AuditService } from './AuditService';

export interface Invite {
  id: string;
  providerId: string;
  providerName: string;
  practiceName: string;
  inquiryId: string;
  clientEmail: string;
  clientName: string;
  selectedDocuments: string[];
  status: 'pending' | 'accepted' | 'expired';
  acceptedBy?: string;
  createdAt: Date;
  expiresAt: Date;
}

const COLLECTION = 'invites';
const EXPIRY_DAYS = 30;

export class FirebaseInviteService {

  /**
   * Generate an invite link for a prospective client
   */
  static async generateInvite(data: {
    providerId: string;
    providerName: string;
    practiceName: string;
    inquiryId: string;
    clientEmail: string;
    clientName: string;
    selectedDocuments: string[];
  }): Promise<string> {
    const docRef = doc(collection(db, COLLECTION));
    const now = Timestamp.now();
    const expiresAt = Timestamp.fromDate(
      new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    );

    await setDoc(docRef, {
      providerId: data.providerId,
      providerName: data.providerName,
      practiceName: data.practiceName,
      inquiryId: data.inquiryId,
      clientEmail: data.clientEmail,
      clientName: data.clientName,
      selectedDocuments: data.selectedDocuments,
      status: 'pending',
      createdAt: now,
      expiresAt: expiresAt,
    });

    // Audit log
    try {
      await AuditService.log({
        action: 'invite_created',
        userId: data.providerId,
        userRole: 'provider',
        resourceType: 'invite',
        resourceId: docRef.id,
        resourceOwner: data.providerId,
        details: `Created invite for ${data.clientName} (${data.clientEmail})`,
      });
    } catch (e) {
      // Non-blocking
    }

    return docRef.id;
  }

  /**
   * Get an invite by ID (used by registration page to resolve provider)
   */
  static async getInvite(inviteId: string): Promise<Invite | null> {
    const docSnap = await getDoc(doc(db, COLLECTION, inviteId));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      id: docSnap.id,
      providerId: data.providerId,
      providerName: data.providerName,
      practiceName: data.practiceName,
      inquiryId: data.inquiryId,
      clientEmail: data.clientEmail,
      clientName: data.clientName,
      selectedDocuments: data.selectedDocuments || [],
      status: data.status,
      acceptedBy: data.acceptedBy,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      expiresAt: data.expiresAt?.toDate?.() || new Date(),
    } as Invite;
  }

  /**
   * Check if an invite is expired
   */
  static isExpired(invite: Invite): boolean {
    return invite.status === 'expired' || new Date() > invite.expiresAt;
  }

  /**
   * Mark an invite as accepted when client completes registration
   */
  static async acceptInvite(inviteId: string, clientUid: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, inviteId), {
      status: 'accepted',
      acceptedBy: clientUid,
    });

    // Audit log
    try {
      await AuditService.log({
        action: 'invite_accepted',
        userId: clientUid,
        userRole: 'client',
        resourceType: 'invite',
        resourceId: inviteId,
        resourceOwner: clientUid,
        details: `Client accepted invite ${inviteId}`,
      });
    } catch (e) {
      // Non-blocking
    }
  }

  /**
   * Build the invite registration URL
   */
  static getInviteUrl(inviteId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/client-register?invite=${inviteId}`;
  }
}
